import { apiRequest } from "../api";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Topic } from "../types/dbTypes";

interface IGlobalProviderProps {
  pingData: {
    svTime: string | Date;
    version: string;
  };
}

type BuildingBlocksDictionary = {
  my: Record<string, Topic | "text">;
  team: Record<string, Topic | "text">;
};

export const useGlobalProvider = ({ pingData }: IGlobalProviderProps) => {
  const [modalTopicViewId, setModalTopicViewId] = useState<string>(null);

  const [view, setView] = useState<"my" | "team" | string | null>(
    localStorage.getItem("viewSelector") || "my"
  );

  const [buildingBlocksDict, setBuildingBlocksDict] =
    useState<BuildingBlocksDictionary>(
      localStorage.getItem("bb_dict")
        ? JSON.parse(localStorage.getItem("bb_dict"))
        : null
    );

  const getAllBuildingBlocks = async (hydrate: boolean = false) => {
    const data = await apiRequest<BuildingBlocksDictionary>({
      subRoute: "roadmap",
      path: "/get_all_building_blocks" + (hydrate ? "?hydrate=true" : ""),
      method: "GET"
    });

    setBuildingBlocksDict(data);
  };

  useEffect(() => {
    const handler = (e: Event) => {
      const { documentId } = (e as CustomEvent).detail;
      setModalTopicViewId(documentId);
    };

    document.body.addEventListener("document-mention-click", handler);
    return () => {
      document.body.removeEventListener("document-mention-click", handler);
    };
  }, []);

  useEffect(() => {
    if (buildingBlocksDict) {
      localStorage.setItem("bb_dict", JSON.stringify(buildingBlocksDict));
    } else {
      localStorage.removeItem("bb_dict");
    }
  }, [buildingBlocksDict]);

  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState<number>(window.innerHeight);

  const isPWA =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator["standalone"] ||
    document.referrer.includes("android-app://");

  const handleWindowSizeChange = () => {
    setWindowWidth(window.innerWidth);
    setWindowHeight(window.innerHeight);
  };

  const isMobile = useMemo(() => {
    return windowWidth <= 768;
  }, [windowWidth]);

  const isSmallMobile = useMemo(() => {
    return windowWidth <= 360;
  }, [windowWidth]);

  const isTouchDevice = useMemo(() => {
    return "ontouchstart" in window || navigator.maxTouchPoints > 0;
  }, []);

  const isTablet = useMemo(() => {
    return windowWidth > 768 && windowWidth <= 1024;
  }, [windowWidth]);

  const isDesktop = useMemo(() => {
    return windowWidth > 1024;
  }, [windowWidth]);

  const isLargeDesktop = useMemo(() => {
    return windowWidth >= 1600;
  }, [windowWidth]);

  const shouldShowMobileLayout = useMemo(() => {
    return windowWidth <= 768;
  }, [windowWidth]);

  const shouldShowTabletLayout = useMemo(() => {
    return windowWidth > 768 && windowWidth <= 1024;
  }, [windowWidth]);

  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  useEffect(() => {
    if (view) {
      localStorage.setItem("viewSelector", view);
    } else {
      localStorage.removeItem("viewSelector");
    }
  }, [view]);

  return {
    pingData,

    windowWidth,
    windowHeight,

    isPWA,
    isMobile,
    isSmallMobile,
    isTouchDevice,
    isTablet,
    isDesktop,
    isLargeDesktop,
    shouldShowMobileLayout,
    shouldShowTabletLayout,

    view,
    setView,

    buildingBlocksDict,
    getAllBuildingBlocks,

    modalTopicViewId,
    setModalTopicViewId
  };
};

export const GlobalProvider = ({ pingData, children }) => {
  const globalProvider = useGlobalProvider({ pingData });

  return (
    <GlobalContext.Provider value={globalProvider}>
      {children}
    </GlobalContext.Provider>
  );
};

export type GlobalProps = ReturnType<typeof useGlobalProvider>;
export const GlobalContext = createContext<GlobalProps>(null);
export const useGlobal = () => useContext(GlobalContext);
